import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ticketAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { metadataAPI } from '../services/api';
import { getStatusBadgeClass, getSeverityPillClass, formatDateTime } from '../utils/helpers';
import toast from 'react-hot-toast';
import {
  ArrowLeft, Clock, User, Users, CheckCircle, XCircle,
  AlertTriangle, FileText, MessageSquare, Paperclip, Upload, Download, Trash2, Pencil
} from 'lucide-react';

export default function TicketDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isStaff, isClient, isAdmin, isSupportLead } = useAuth();

  const [ticket, setTicket] = useState(null);
  const [audit, setAudit] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Workflow form state
  const [triageNotes, setTriageNotes] = useState('');
  const [groups, setGroups] = useState([]);
  const [resolverGroupId, setResolverGroupId] = useState('');
  const [verifierGroupId, setVerifierGroupId] = useState('');
  const [resolverIndId, setResolverIndId] = useState('');
  const [verifierIndId, setVerifierIndId] = useState('');
  const [edc, setEdc] = useState('');
  const [resolutionText, setResolutionText] = useState('');
  const [verificationText, setVerificationText] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [reassignNotes, setReassignNotes] = useState('');

  // Edit state
  const [editing, setEditing] = useState(false);
  const [editObserved, setEditObserved] = useState('');
  const [editExpected, setEditExpected] = useState('');

  // Classification edit state
  const [editingClassification, setEditingClassification] = useState(false);
  const [editIssueTypeId, setEditIssueTypeId] = useState('');
  const [editFeature, setEditFeature] = useState('');
  const [editSubFeature, setEditSubFeature] = useState('');
  const [editModule, setEditModule] = useState('');
  const [editMart, setEditMart] = useState('');
  const [editImpactId, setEditImpactId] = useState('');
  const [editSeverityId, setEditSeverityId] = useState('');
  const [issueTypes, setIssueTypes] = useState([]);
  const [features, setFeatures] = useState([]);
  const [impactLevels, setImpactLevels] = useState([]);
  const [severityLevels, setSeverityLevels] = useState([]);
  const [modules, setModules] = useState([]);
  const [marts, setMarts] = useState([]);

  // File upload state for resolution/verification
  const [resolutionFiles, setResolutionFiles] = useState([]);
  const [verificationFiles, setVerificationFiles] = useState([]);
  const resFileRef = useRef(null);
  const verFileRef = useRef(null);

  const loadTicket = useCallback(async () => {
    try {
      const [ticketResp, auditResp, attachResp] = await Promise.all([
        ticketAPI.get(id),
        ticketAPI.getAudit(id),
        ticketAPI.getAttachments(id),
      ]);
      setTicket(ticketResp.data);
      setAudit(auditResp.data);
      setAttachments(attachResp.data);
    } catch (err) {
      toast.error('Failed to load ticket');
      navigate('/tickets');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { loadTicket(); }, [loadTicket]);

  useEffect(() => {
    if (isStaff) {
      metadataAPI.getGroups().then(r => setGroups(r.data)).catch(() => {});
    }
  }, [isStaff]);

  const doAction = async (actionFn, data, successMsg) => {
    setActionLoading(true);
    try {
      await actionFn(id, data);
      toast.success(successMsg);
      await loadTicket();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const startClassificationEdit = async () => {
    // Load all metadata dropdowns
    let itData = [], fData = [], impData = [], sevData = [];
    try {
      const [itResp, fResp, impResp, sevResp] = await Promise.all([
        metadataAPI.getIssueTypes(), metadataAPI.getFeatures(),
        metadataAPI.getImpactLevels(), metadataAPI.getSeverityLevels(),
      ]);
      itData = itResp.data; fData = fResp.data; impData = impResp.data; sevData = sevResp.data;
      setIssueTypes(itData); setFeatures(fData);
      setImpactLevels(impData); setSeverityLevels(sevData);
      if (ticket.client_id) {
        const [modResp, martResp] = await Promise.all([
          metadataAPI.getClientModules(ticket.client_id),
          metadataAPI.getClientMarts(ticket.client_id),
        ]);
        setModules(modResp.data); setMarts(martResp.data);
      }
    } catch { /* dropdowns may be partial */ }
    // Populate edit fields from current ticket using fetched data
    const it = ticket;
    setEditIssueTypeId(itData.find(t => t.name === it.issue_type)?.id || '');
    setEditFeature(it.feature || ''); setEditSubFeature(it.sub_feature || '');
    setEditModule(it.module || ''); setEditMart(it.mart || '');
    setEditImpactId(impData.find(i => i.name === it.impact)?.id || '');
    setEditSeverityId(sevData.find(s => s.name === it.severity)?.id || '');
    setEditingClassification(true);
  };

  const uploadFiles = async (files, stage) => {
    let uploaded = 0;
    for (const file of files) {
      try {
        await ticketAPI.uploadAttachment(id, file, stage);
        uploaded++;
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    return uploaded;
  };

  const handleCompleteResolution = async () => {
    setActionLoading(true);
    try {
      // Upload resolution files first, then complete resolution
      if (resolutionFiles.length > 0) {
        await uploadFiles(resolutionFiles, 'RESOLUTION');
        setResolutionFiles([]);
      }
      await ticketAPI.completeResolution(id, { resolution_text: resolutionText });
      toast.success('Resolution completed');
      await loadTicket();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerify = async () => {
    setActionLoading(true);
    try {
      if (verificationFiles.length > 0) {
        await uploadFiles(verificationFiles, 'VERIFICATION');
        setVerificationFiles([]);
      }
      await ticketAPI.verify(id, { verification_text: verificationText });
      toast.success('Resolution verified and communicated');
      await loadTicket();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownload = async (attachmentId) => {
    try {
      // Stream file through backend with auth token
      const token = localStorage.getItem('access_token');
      const resp = await fetch(`/api/tickets/${id}/attachments/${attachmentId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Download failed');
      const blob = await resp.blob();
      const filename = resp.headers.get('content-disposition')
        ?.match(/filename="(.+)"/)?.[1] || 'download';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  // Get members of a specific group
  const getGroupMembers = (groupId) => {
    const group = groups.find(g => String(g.id) === String(groupId));
    return group?.members || [];
  };

  if (loading) return <p style={{ padding: 40, color: 'var(--color-text-muted)' }}>Loading ticket...</p>;
  if (!ticket) return <p style={{ padding: 40 }}>Ticket not found</p>;

  const t = ticket;
  const canTriage = isStaff && (isAdmin || isSupportLead) && t.status === 'OPEN';
  const canAssignGroup = isStaff && (isAdmin || isSupportLead) && t.status === 'TRIAGED';
  // Only leads of the assigned group, CTO, or Admin can assign individuals
  const userGroups = user?.groups || [];
  // Determine which group(s) the current user leads
  const leadRoles = ['QA_LEAD', 'SUPP_LEAD', 'ENGG_LEAD', 'OPS_LEAD', 'ETL_LEAD'];
  const isLead = leadRoles.includes(user?.role);
  const userLeadGroupIds = (() => {
    if (!isLead) return [];
    return groups.filter(g => userGroups.includes(g.group_code)).map(g => g.id);
  })();
  // Can assign resolver if: admin/CTO, OR lead of the resolver group AND resolver not yet assigned
  const canAssignResolver = isStaff && t.status === 'ASSIGNED_GROUP'
      && (isAdmin || (isLead && userLeadGroupIds.includes(t.resolver_group_id)))
      && !t.resolver_individual_id;
  // Can assign verifier if: admin/CTO, OR lead of the verifier group AND verifier not yet assigned
  const canAssignVerifier = isStaff && t.status === 'ASSIGNED_GROUP'
      && (isAdmin || (isLead && userLeadGroupIds.includes(t.verifier_group_id)))
      && !t.verifier_individual_id;
  const canAssignIndividual = canAssignResolver || canAssignVerifier;
  const canCommence = isStaff && t.status === 'ASSIGNED_INDIVIDUAL'
      && (isAdmin || user?.id === t.resolver_individual_id);
  const canComplete = isStaff && t.status === 'RESOLUTION_COMMENCED'
      && (isAdmin || user?.id === t.resolver_individual_id);
  const canVerify = isStaff && t.status === 'RESOLUTION_COMPLETED'
      && (isAdmin || user?.id === t.verifier_individual_id);
  const isStaffCreator = isStaff && t.created_by_staff_id && user?.id === t.created_by_staff_id;
  const canClose = t.status === 'COMMUNICATED' && ((isClient && user?.id === t.reporter_user_id) || isAdmin || isStaffCreator);
  const canReject = canClose;
  // Edit: reporter or staff creator can edit in OPEN/TRIAGED/ASSIGNED_GROUP
  const editableStatuses = ['OPEN', 'TRIAGED', 'ASSIGNED_GROUP'];
  const canEdit = editableStatuses.includes(t.status)
      && ((isClient && user?.id === t.reporter_user_id) || isStaffCreator || isAdmin);
  const canEditClassification = t.status === 'OPEN' && canEdit;
  // Reassign: group-level (Support Lead/CTO/Admin), individual-level (any lead/CTO/Admin)
  const reassignableStatuses = ['ASSIGNED_GROUP', 'ASSIGNED_INDIVIDUAL', 'RESOLUTION_COMMENCED'];
  const canReassignGroup = isStaff && reassignableStatuses.includes(t.status) && (isAdmin || isSupportLead);
  const canReassignIndividual = isStaff && reassignableStatuses.includes(t.status) && (isAdmin || isSupportLead || isLead);
  // Scoped individual reassignment: which buttons to show
  const canReassignResolver = canReassignIndividual && t.resolver_individual_id
      && (isAdmin || isSupportLead || (isLead && userLeadGroupIds.includes(t.resolver_group_id)));
  const canReassignVerifier = canReassignIndividual && t.verifier_individual_id
      && (isAdmin || isSupportLead || (isLead && userLeadGroupIds.includes(t.verifier_group_id)));
  // Client can withdraw OPEN tickets; staff creator can close at any status; Admin/CTO can force-close any non-CLOSED
  const canWithdraw = (isClient && user?.id === t.reporter_user_id && t.status === 'OPEN')
      || (isStaffCreator && t.status !== 'CLOSED')
      || (isAdmin && t.status !== 'CLOSED');
  const canDelete = (isClient && user?.id === t.reporter_user_id && t.status === 'OPEN')
      || (isStaffCreator && t.status === 'OPEN');

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button className="btn btn-sm" onClick={() => navigate('/tickets')}>
          <ArrowLeft size={14} /> Back
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 style={{ fontSize: 20, fontWeight: 600 }}>{t.issue_id}</h1>
            <span className={`badge ${getStatusBadgeClass(t.status_display)}`}>{t.status_display}</span>
            {t.is_sla_breached && (
              <span className="badge pill-critical" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <AlertTriangle size={12} /> SLA breached
              </span>
            )}
          </div>
          <p style={{ fontSize: 13, color: 'var(--color-text-muted)', marginTop: 4 }}>
            Raised by {t.reporter_name} ({t.reporter_email}) on {formatDateTime(t.created_at)}
          </p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 20, alignItems: 'start' }}>
        {/* Left: Ticket detail */}
        <div>
          {/* Classification */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div className="card-title" style={{ margin: 0 }}>Classification</div>
              {canEditClassification && !editingClassification && (
                <button className="btn btn-sm" onClick={startClassificationEdit} style={{ fontSize: 11 }}>
                  <Pencil size={12} /> Edit
                </button>
              )}
            </div>
            {editingClassification ? (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label className="form-label">Issue type</label>
                    <select className="form-select" value={editIssueTypeId}
                      onChange={e => setEditIssueTypeId(e.target.value)}>
                      <option value="">Select...</option>
                      {issueTypes.map(it => <option key={it.id} value={it.id}>{it.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Feature</label>
                    <select className="form-select" value={editFeature}
                      onChange={e => { setEditFeature(e.target.value); setEditSubFeature(''); }}>
                      <option value="">Select...</option>
                      {features.map(f => <option key={f.id} value={f.name}>{f.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Sub-feature</label>
                    <input className="form-input" value={editSubFeature}
                      onChange={e => setEditSubFeature(e.target.value)} placeholder="Sub-feature" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Module</label>
                    <select className="form-select" value={editModule}
                      onChange={e => setEditModule(e.target.value)}>
                      <option value="">Select...</option>
                      {modules.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Mart</label>
                    <select className="form-select" value={editMart}
                      onChange={e => setEditMart(e.target.value)}>
                      <option value="">Select (optional)...</option>
                      {marts.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Impact</label>
                    <select className="form-select" value={editImpactId}
                      onChange={e => setEditImpactId(e.target.value)}>
                      <option value="">Select...</option>
                      {impactLevels.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Severity</label>
                    <select className="form-select" value={editSeverityId}
                      onChange={e => setEditSeverityId(e.target.value)}>
                      <option value="">Select...</option>
                      {severityLevels.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button className="btn btn-primary btn-sm" disabled={actionLoading}
                    onClick={async () => {
                      setActionLoading(true);
                      try {
                        const payload = {};
                        if (editIssueTypeId) payload.issue_type_id = parseInt(editIssueTypeId);
                        if (editFeature) payload.feature = editFeature;
                        payload.sub_feature = editSubFeature || null;
                        if (editModule) payload.module = editModule;
                        payload.mart = editMart || null;
                        if (editImpactId) payload.impact_id = parseInt(editImpactId);
                        if (editSeverityId) payload.severity_id = parseInt(editSeverityId);
                        await ticketAPI.update(id, payload);
                        toast.success('Classification updated');
                        setEditingClassification(false);
                        await loadTicket();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Update failed');
                      } finally {
                        setActionLoading(false);
                      }
                    }}>Save</button>
                  <button className="btn btn-sm" onClick={() => setEditingClassification(false)}>Cancel</button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Field label="Issue type" value={t.issue_type} />
                <Field label="Feature" value={t.feature ? `${t.feature}${t.sub_feature ? ' / ' + t.sub_feature : ''}` : '-'} />
                <Field label="Module" value={t.module || '-'} />
                <Field label="Mart" value={t.mart || '-'} />
                <Field label="Impact" value={<span className={`badge ${getSeverityPillClass(t.impact)}`}>{t.impact}</span>} />
                <Field label="Severity" value={<span className={`badge ${getSeverityPillClass(t.severity)}`}>{t.severity}</span>} />
              </div>
            )}
          </div>

          {/* Description */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div className="card-title" style={{ margin: 0 }}>Description</div>
              {canEdit && !editing && (
                <button className="btn btn-sm" onClick={() => {
                  setEditing(true);
                  setEditObserved(t.description?.observed || '');
                  setEditExpected(t.description?.expected || '');
                }} style={{ fontSize: 11 }}>
                  <Pencil size={12} /> Edit
                </button>
              )}
            </div>
            {editing ? (
              <div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>Observed behaviour</label>
                  <textarea className="form-textarea" rows={3} value={editObserved}
                    onChange={e => setEditObserved(e.target.value)} style={{ marginTop: 4 }} />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>Expected behaviour</label>
                  <textarea className="form-textarea" rows={3} value={editExpected}
                    onChange={e => setEditExpected(e.target.value)} style={{ marginTop: 4 }} />
                </div>
                {t.status !== 'OPEN' && (
                  <p style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 8 }}>
                    Only description can be edited after triage. Classification changes require the ticket to be in Open status.
                  </p>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-primary btn-sm" disabled={actionLoading}
                    onClick={async () => {
                      setActionLoading(true);
                      try {
                        await ticketAPI.update(id, { description: { observed: editObserved, expected: editExpected } });
                        toast.success('Description updated');
                        setEditing(false);
                        await loadTicket();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Update failed');
                      } finally {
                        setActionLoading(false);
                      }
                    }}>Save</button>
                  <button className="btn btn-sm" onClick={() => setEditing(false)}>Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>Observed behaviour</label>
                  <p style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{t.description?.observed}</p>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>Expected behaviour</label>
                  <p style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{t.description?.expected}</p>
                </div>
              </>
            )}
            <AttachmentList attachments={attachments} stage="CREATION" label="Attachments" onDownload={handleDownload} />
          </div>

          {/* Resolution (if exists) */}
          {t.resolution_text && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-title" style={{ marginBottom: 8 }}>Resolution</div>
              <p style={{ whiteSpace: 'pre-wrap' }}>{t.resolution_text}</p>
              {t.resolution_completed_at && (
                <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 8 }}>
                  Completed: {formatDateTime(t.resolution_completed_at)}
                </p>
              )}
              <AttachmentList attachments={attachments} stage="RESOLUTION" label="Resolution evidence" onDownload={handleDownload} />
            </div>
          )}

          {/* Verification (if exists) */}
          {t.verification_text && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-title" style={{ marginBottom: 8 }}>Verification</div>
              <p style={{ whiteSpace: 'pre-wrap' }}>{t.verification_text}</p>
              {t.verification_at && (
                <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 8 }}>
                  Verified: {formatDateTime(t.verification_at)}
                </p>
              )}
              <AttachmentList attachments={attachments} stage="VERIFICATION" label="Verification evidence" onDownload={handleDownload} />
            </div>
          )}

          {/* Rejection reason */}
          {t.rejection_reason && (
            <div className="card" style={{ marginBottom: 16, borderLeftColor: 'var(--color-danger)', borderLeftWidth: 3 }}>
              <div className="card-title" style={{ color: 'var(--color-danger)', marginBottom: 8 }}>Rejection reason</div>
              <p>{t.rejection_reason}</p>
            </div>
          )}

          {/* All attachments — standalone section ensures nothing is hidden */}
          {attachments.length > 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-title" style={{ marginBottom: 12 }}>All attachments</div>
              {['CREATION', 'RESOLUTION', 'VERIFICATION', 'REJECTION'].map(stage => {
                const stageFiles = attachments.filter(a => a.upload_stage === stage);
                if (stageFiles.length === 0) return null;
                const stageLabels = { CREATION: 'Ticket creation', RESOLUTION: 'Resolution evidence', VERIFICATION: 'Verification evidence', REJECTION: 'Rejection' };
                return <AttachmentList key={stage} attachments={attachments} stage={stage}
                  label={stageLabels[stage] || stage} onDownload={handleDownload} />;
              })}
            </div>
          )}

          {/* Audit trail */}
          <div className="card">
            <div className="card-title" style={{ marginBottom: 12 }}>Audit trail</div>
            {audit.length === 0 ? (
              <p style={{ color: 'var(--color-text-muted)' }}>No audit entries</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {audit.map(a => {
                  const nv = a.new_value ? (() => { try { return JSON.parse(a.new_value); } catch { return null; } })() : null;
                  return (
                  <div key={a.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid var(--color-border)' }}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--color-primary)', marginTop: 6, flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13 }}>
                        <strong>{a.action}</strong>
                        {/* Show performer: always for staff; for clients only on their own actions */}
                        {(isStaff || ['CREATED', 'CLOSED', 'REJECTED', 'WITHDRAWN'].includes(a.action)) && (
                          <>{' '}by {a.performed_by_name}</>
                        )}
                        {isStaff && nv?.assigned_to && <span style={{ color: 'var(--color-text-muted)' }}>. Assigned to {nv.assigned_to}</span>}
                      </div>
                      {/* Scenario 2: both assigned at once — show resolver + verifier on separate line */}
                      {isStaff && nv?.resolver_name && nv?.verifier_name && (
                        <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2 }}>
                          Resolver: {nv.resolver_name} &middot; Verifier: {nv.verifier_name}
                        </p>
                      )}
                      {a.notes && <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2 }}>{a.notes}</p>}
                      <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{formatDateTime(a.performed_at)}</span>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right: Workflow actions & info panel */}
        <div>
          {/* Key dates */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-title" style={{ marginBottom: 12 }}>Timeline</div>
            <TimelineRow icon={FileText} label="Created" value={formatDateTime(t.created_at)} />
            {isStaff && t.triage_flag && <TimelineRow icon={CheckCircle} label="Triaged" value={formatDateTime(t.triage_at)} />}
            {isStaff && t.resolver_individual && <TimelineRow icon={User} label="Resolver" value={t.resolver_individual} />}
            {isStaff && t.verifier_individual && <TimelineRow icon={User} label="Verifier" value={t.verifier_individual} />}
            {t.edc && <TimelineRow icon={Clock} label="EDC" value={formatDateTime(t.edc)} />}
            {t.communicated_at && <TimelineRow icon={MessageSquare} label="Communicated" value={formatDateTime(t.communicated_at)} />}
            {t.closed_at && <TimelineRow icon={CheckCircle} label="Closed" value={formatDateTime(t.closed_at)} />}
            {t.sla_deadline && (
                <TimelineRow icon={AlertTriangle} label="SLA deadline"
                             value={formatDateTime(t.sla_deadline)} />
            )}
            {t.elapsed_hours != null && (
              <TimelineRow icon={Clock} label="Elapsed" value={`${t.elapsed_hours}h`} />
            )}
          </div>

          {/* Workflow actions (staff only, step-gated) */}
          {isStaff && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-title" style={{ marginBottom: 12 }}>Actions</div>

              {canTriage && (
                <ActionSection title="Triage" onSubmit={() => doAction(ticketAPI.triage, { notes: triageNotes }, 'Ticket triaged')}>
                  <textarea className="form-textarea" rows={2} placeholder="Triage notes (optional)" value={triageNotes} onChange={e => setTriageNotes(e.target.value)} />
                  <button className="btn btn-primary btn-sm" disabled={actionLoading} style={{ marginTop: 8 }}
                          onClick={() => doAction(ticketAPI.triage, { notes: triageNotes }, 'Ticket triaged')}>Mark as triaged</button>
                </ActionSection>
              )}

              {canAssignGroup && (
                <ActionSection title="Assign to groups">
                  <div className="form-group">
                    <label className="form-label">Resolver group</label>
                    <select className="form-select" value={resolverGroupId} onChange={e => setResolverGroupId(e.target.value)}>
                      <option value="">Select...</option>
                      {groups.map(g => <option key={g.id} value={g.id}>{g.group_name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Verifier group</label>
                    <select className="form-select" value={verifierGroupId} onChange={e => setVerifierGroupId(e.target.value)}>
                      <option value="">Select...</option>
                      {groups.map(g => <option key={g.id} value={g.id}>{g.group_name}</option>)}
                    </select>
                  </div>
                  <button className="btn btn-primary btn-sm" disabled={actionLoading || !resolverGroupId || !verifierGroupId}
                    onClick={() => doAction(ticketAPI.assignGroup, { resolver_group_id: parseInt(resolverGroupId), verifier_group_id: parseInt(verifierGroupId) }, 'Groups assigned')}>
                    Assign groups
                  </button>
                </ActionSection>
              )}

              {canAssignIndividual && (
                <ActionSection title="Assign individuals">
                  {/* Resolver assignment */}
                  {canAssignResolver ? (
                    <div className="form-group">
                      <label className="form-label">Resolver ({groups.find(g => g.id === t.resolver_group_id)?.group_name})</label>
                      <select className="form-select" value={resolverIndId} onChange={e => setResolverIndId(e.target.value)}>
                        <option value="">Select resolver...</option>
                        {getGroupMembers(t.resolver_group_id).map(m =>
                          <option key={m.id} value={m.id}>{m.name} ({m.email})</option>
                        )}
                      </select>
                    </div>
                  ) : t.resolver_individual ? (
                    <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 12 }}>
                      Resolver: <strong style={{ color: 'var(--color-text)' }}>{t.resolver_individual}</strong> ✓
                    </div>
                  ) : (
                    <div style={{ fontSize: 13, color: 'var(--color-warning)', marginBottom: 12 }}>
                      Waiting for {groups.find(g => g.id === t.resolver_group_id)?.group_name} lead to assign resolver
                    </div>
                  )}

                  {/* Verifier assignment */}
                  {canAssignVerifier ? (
                    <div className="form-group">
                      <label className="form-label">Verifier ({groups.find(g => g.id === t.verifier_group_id)?.group_name})</label>
                      <select className="form-select" value={verifierIndId} onChange={e => setVerifierIndId(e.target.value)}>
                        <option value="">Select verifier...</option>
                        {getGroupMembers(t.verifier_group_id).map(m =>
                          <option key={m.id} value={m.id}>{m.name} ({m.email})</option>
                        )}
                      </select>
                    </div>
                  ) : t.verifier_individual ? (
                    <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 12 }}>
                      Verifier: <strong style={{ color: 'var(--color-text)' }}>{t.verifier_individual}</strong> ✓
                    </div>
                  ) : (
                    <div style={{ fontSize: 13, color: 'var(--color-warning)', marginBottom: 12 }}>
                      Waiting for {groups.find(g => g.id === t.verifier_group_id)?.group_name} lead to assign verifier
                    </div>
                  )}

                  <button className="btn btn-primary btn-sm"
                    disabled={actionLoading || (canAssignResolver && !resolverIndId) || (canAssignVerifier && !verifierIndId)}
                    onClick={() => {
                      const payload = {};
                      if (canAssignResolver && resolverIndId) payload.resolver_individual_id = parseInt(resolverIndId);
                      if (canAssignVerifier && verifierIndId) payload.verifier_individual_id = parseInt(verifierIndId);
                      doAction(ticketAPI.assignIndividual, payload, 'Individual assigned');
                    }}>
                    Assign
                  </button>
                </ActionSection>
              )}

              {canCommence && (
                <ActionSection title="Commence resolution">
                  <div className="form-group">
                    <label className="form-label">Expected date of completion</label>
                    <input type="datetime-local" className="form-input" value={edc} onChange={e => setEdc(e.target.value)} />
                  </div>
                  <button className="btn btn-primary btn-sm" disabled={actionLoading || !edc}
                    onClick={() => doAction(ticketAPI.commenceResolution, { edc: new Date(edc).toISOString() }, 'Resolution commenced')}>
                    Start resolution
                  </button>
                </ActionSection>
              )}

              {canComplete && (
                <ActionSection title="Complete resolution">
                  <textarea className="form-textarea" rows={3} placeholder="Resolution steps and root cause analysis..." value={resolutionText} onChange={e => setResolutionText(e.target.value)} />
                  <FileUploadInline
                    files={resolutionFiles}
                    setFiles={setResolutionFiles}
                    inputRef={resFileRef}
                    label="Attach resolution evidence"
                  />
                  <button className="btn btn-primary btn-sm" disabled={actionLoading || !resolutionText} style={{ marginTop: 8 }}
                    onClick={handleCompleteResolution}>
                    Mark resolved
                  </button>
                </ActionSection>
              )}

              {canVerify && (
                <ActionSection title="Verify resolution">
                  <textarea className="form-textarea" rows={3} placeholder="Verification steps..." value={verificationText} onChange={e => setVerificationText(e.target.value)} />
                  <FileUploadInline
                    files={verificationFiles}
                    setFiles={setVerificationFiles}
                    inputRef={verFileRef}
                    label="Attach verification evidence"
                  />
                  <button className="btn btn-primary btn-sm" disabled={actionLoading || !verificationText} style={{ marginTop: 8 }}
                    onClick={handleVerify}>
                    Verify and communicate
                  </button>
                </ActionSection>
              )}

              {!canTriage && !canAssignGroup && !canAssignIndividual && !canCommence && !canComplete && !canVerify
                && !canReassignGroup && !canReassignResolver && !canReassignVerifier && (
                <div>
                  <p style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>No actions available at this stage.</p>
                  {/* Resolver is assigned but verifier not yet — explain the wait */}
                  {t.status === 'ASSIGNED_GROUP' && t.resolver_individual_id && user?.id === t.resolver_individual_id && !t.verifier_individual_id && (
                    <p style={{ fontSize: 12, color: 'var(--color-warning, #d97706)', marginTop: 8, padding: '8px 12px',
                      background: 'var(--color-bg-subtle, #fffbeb)', borderRadius: 6, border: '1px solid var(--color-warning, #d97706)' }}>
                      You have been assigned as the <strong>resolver</strong> for this ticket.
                      Resolution can begin once the verifier group lead assigns a verifier.
                    </p>
                  )}
                  {/* Verifier is assigned but resolver not yet */}
                  {t.status === 'ASSIGNED_GROUP' && t.verifier_individual_id && user?.id === t.verifier_individual_id && !t.resolver_individual_id && (
                    <p style={{ fontSize: 12, color: 'var(--color-warning, #d97706)', marginTop: 8, padding: '8px 12px',
                      background: 'var(--color-bg-subtle, #fffbeb)', borderRadius: 6, border: '1px solid var(--color-warning, #d97706)' }}>
                      You have been assigned as the <strong>verifier</strong> for this ticket.
                      The resolver group lead still needs to assign a resolver before work can begin.
                    </p>
                  )}
                </div>
              )}

              {/* Reassignment */}
              {(canReassignGroup || canReassignResolver || canReassignVerifier) && (
                <ActionSection title="Reassign">
                  <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 8 }}>
                    Reassign this ticket if the original assignment was incorrect or the assignee is unavailable.
                  </p>
                  <textarea className="form-textarea" rows={2} placeholder="Reason for reassignment"
                    value={reassignNotes} onChange={e => setReassignNotes(e.target.value)} />
                  <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                    {canReassignResolver && (
                      <button className="btn btn-sm" disabled={actionLoading}
                        onClick={() => {
                          if (!window.confirm(`This will clear the resolver assignment (${t.resolver_individual}). The resolver group lead will need to assign a new resolver. Continue?`)) return;
                          doAction(ticketAPI.reassign,
                            { level: 'individual', scope: 'resolver', notes: reassignNotes },
                            'Resolver reassigned — awaiting new resolver assignment');
                        }}>
                        Reassign resolver
                      </button>
                    )}
                    {canReassignVerifier && (
                      <button className="btn btn-sm" disabled={actionLoading}
                        onClick={() => {
                          if (!window.confirm(`This will clear the verifier assignment (${t.verifier_individual}). The verifier group lead will need to assign a new verifier. Continue?`)) return;
                          doAction(ticketAPI.reassign,
                            { level: 'individual', scope: 'verifier', notes: reassignNotes },
                            'Verifier reassigned — awaiting new verifier assignment');
                        }}>
                        Reassign verifier
                      </button>
                    )}
                    {canReassignGroup && (
                      <button className="btn btn-sm btn-danger" disabled={actionLoading}
                        onClick={() => {
                          if (!window.confirm('This will clear group AND individual assignments. The ticket returns to Triaged status. Continue?')) return;
                          doAction(ticketAPI.reassign,
                            { level: 'group', notes: reassignNotes },
                            'Ticket reassigned — groups cleared, ready for re-triage');
                        }}>
                        Reassign groups
                      </button>
                    )}
                  </div>
                </ActionSection>
              )}
            </div>
          )}

          {/* Withdraw / Force-close */}
          {canWithdraw && !canClose && (
              <div className="card" style={{ marginBottom: 16 }}>
                <div className="card-title" style={{ marginBottom: 12 }}>
                  {isClient ? 'Withdraw ticket' : isStaffCreator ? 'Close ticket' : 'Force close'}
                </div>
                <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 8 }}>
                  {isClient
                      ? 'Withdraw this ticket if it was raised by mistake.'
                      : isStaffCreator
                      ? `Close this ticket on behalf of the client (current status: ${t.status_display}). Attach the client\'s email confirmation if available.`
                      : `Force-close this ticket (current status: ${t.status_display}).`}
                </p>
                <textarea className="form-textarea" rows={2}
                          placeholder={isClient ? 'Reason for withdrawal (optional)' : 'Reason for closure (optional)'}
                          value={rejectionReason} onChange={e => setRejectionReason(e.target.value)} />
                <button className="btn btn-danger btn-sm" disabled={actionLoading} style={{ marginTop: 8 }}
                        onClick={() => doAction(ticketAPI.withdraw, { notes: rejectionReason },
                            isClient ? 'Ticket withdrawn' : isStaffCreator ? 'Ticket closed' : 'Ticket force-closed')}>
                  <XCircle size={14} /> {isClient ? 'Withdraw' : isStaffCreator ? 'Close ticket' : 'Force close'}
                </button>
              </div>
          )}
          {/* Client close/reject (after verification) */}
          {(canClose || canReject) && (
            <div className="card">
              <div className="card-title" style={{ marginBottom: 12 }}>Your response</div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <button className="btn btn-primary btn-sm" disabled={actionLoading}
                  onClick={() => doAction(ticketAPI.close, {}, 'Ticket closed')}>
                  <CheckCircle size={14} /> Close ticket
                </button>
                <button className="btn btn-danger btn-sm" disabled={actionLoading}
                  onClick={() => {
                    if (!rejectionReason) { toast.error('Please enter a rejection reason'); return; }
                    doAction(ticketAPI.reject, { rejection_reason: rejectionReason }, 'Ticket rejected — sent back for re-work');
                  }}>
                  <XCircle size={14} /> Reject
                </button>
              </div>
              <textarea className="form-textarea" rows={2} placeholder="Rejection reason (required for reject)" value={rejectionReason} onChange={e => setRejectionReason(e.target.value)} />
            </div>
          )}
          {canDelete && (
              <div className="card" style={{ marginBottom: 16 }}>
                <div className="card-title" style={{ marginBottom: 12 }}>Delete ticket</div>
                <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 8 }}>
                  Permanently delete this ticket if it was raised by mistake.
                </p>
                <button className="btn btn-danger btn-sm" disabled={actionLoading}
                        onClick={async () => {
                          if (!window.confirm('Are you sure you want to delete this ticket? This cannot be undone.')) return;
                          setActionLoading(true);
                          try {
                            await ticketAPI.delete(id);
                            toast.success('Ticket deleted');
                            navigate('/tickets');
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Delete failed');
                          } finally {
                            setActionLoading(false);
                          }
                        }}>
                  <Trash2 size={14} /> Delete ticket
                </button>
              </div>
          )}
        </div>
      </div>
    </div>
  );
}


// --- Sub-components ---

function FileUploadInline({ files, setFiles, inputRef, label }) {
  const handleSelect = (e) => {
    const selected = Array.from(e.target.files).filter(f => {
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} exceeds 10MB`); return false; }
      return true;
    });
    setFiles(prev => [...prev, ...selected]);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div style={{ marginTop: 8 }}>
      <input type="file" ref={inputRef} onChange={handleSelect} multiple style={{ display: 'none' }}
        accept="image/*,.pdf,.doc,.docx,.xlsx,.csv,.txt,.log,.zip" />
      <button type="button" className="btn btn-sm"
        onClick={() => inputRef.current?.click()}
        style={{ fontSize: 11 }}
      >
        <Upload size={12} /> {label || 'Attach files'}
      </button>
      {files.length > 0 && (
        <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {files.map((f, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--color-text-muted)' }}>
              <Paperclip size={10} />
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
              <button type="button" onClick={() => setFiles(prev => prev.filter((_, i) => i !== idx))}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', padding: 0, fontSize: 11 }}>
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div style={{ marginBottom: 4 }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>{label}</label>
      <div style={{ fontSize: 13, marginTop: 2 }}>{value}</div>
    </div>
  );
}

function TimelineRow({ icon: Icon, label, value }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 13 }}>
      <Icon size={14} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
      <span style={{ color: 'var(--color-text-muted)', minWidth: 90 }}>{label}</span>
      <span style={{ fontWeight: 500 }}>{value}</span>
    </div>
  );
}

function ActionSection({ title, children, onSubmit }) {
  return (
    <div style={{ padding: '12px 0', borderBottom: '1px solid var(--color-border)' }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>{title}</div>
      {children}
    </div>
  );
}

function AttachmentList({ attachments, stage, label, onDownload }) {
  const filtered = attachments.filter(a => a.upload_stage === stage);
  if (filtered.length === 0) return null;
  return (
    <div style={{ marginTop: 12, padding: '10px 12px', background: 'var(--color-bg-subtle, #f9fafb)', borderRadius: 6 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Paperclip size={12} /> {label} ({filtered.length})
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {filtered.map(a => (
          <div key={a.id} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '6px 8px', background: 'var(--color-bg, #fff)',
            borderRadius: 4, fontSize: 13,
          }}>
            <Paperclip size={12} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {a.original_filename}
              </div>
              <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                {(a.file_size / 1024).toFixed(0)} KB • {formatDateTime(a.uploaded_at)}
              </div>
            </div>
            <button className="btn btn-sm" onClick={() => onDownload(a.id)} style={{ flexShrink: 0 }}>
              <Download size={12} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
